/**
 * The tag picker over the shared artwork tag dictionary
 * (`DOC_TYPES.ARTWORK_TAGS`). Values are tag IDS, labels are tag text.
 *
 * Search is the point: the dictionary is global and grows, so the editor needs
 * to find "Oil paint" before someone types "oil paint" again. A search term
 * with no exact match gets a "Create" row in the dropdown footer, which writes
 * the dictionary entry and then selects the returned id.
 *
 * Creation is gated on `onCreateTag`: the toolbar filter omits it, so filtering
 * can never invent vocabulary. NOT `CreatableOptionSelect`, which is hard-wired
 * to `DOC_TYPES.TAGS | ALLERGIES` (student logistics tags) and would pollute
 * the student and user pickers.
 */
import { PlusOutlined } from "@ant-design/icons"
import { App as AntdApp, Button, Divider, Select } from "antd"
import { uniqBy } from "lodash"
import { useCallback, useMemo, useState } from "react"

import { type Uuid } from "#customTypes/globalTypes"
import {
  type ArtworkTagOption,
  normalizeArtworkTagText,
} from "#utils/artworkTagHelpers"

export interface ArtworkTagSelectProps {
  /** Selected tag ids. */
  value: readonly Uuid[]
  onChange: (tagIds: Uuid[]) => void
  /** Dictionary entries in display order (most-used first). */
  options: readonly ArtworkTagOption[]
  /** Omit to hide the create affordance entirely (the filter does). */
  onCreateTag?: (text: string) => Promise<Uuid | null>
  placeholder?: string
  ariaLabel: string
  /** antd Select does not forward data-*, so the wrapper span carries it. */
  testId: string
  disabled?: boolean
  allowClear?: boolean
  style?: React.CSSProperties
}

const ArtworkTagSelect = ({
  value,
  onChange,
  options,
  onCreateTag,
  placeholder,
  ariaLabel,
  testId,
  disabled,
  allowClear,
  style,
}: ArtworkTagSelectProps) => {
  const { message } = AntdApp.useApp()
  const [searchText, setSearchText] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  /**
   * Bridges the gap between `onCreateTag` resolving and the dictionary
   * subscription delivering the new doc. Without it the Select briefly has a
   * selected id with no option and renders the raw id as the chip label. The
   * duplicate is harmless once the listener catches up.
   */
  const [justCreated, setJustCreated] = useState<ArtworkTagOption[]>([])

  const selectOptions = useMemo(
    () =>
      uniqBy([...options, ...justCreated], (option) => option.id).map(
        (option) => ({ value: option.id, label: option.text })
      ),
    [justCreated, options]
  )

  const pendingCreateText = useMemo(() => {
    if (!onCreateTag) return ""
    const normalized = normalizeArtworkTagText(searchText)
    if (!normalized) return ""
    const key = normalized.toLowerCase()
    const exists = selectOptions.some(
      (option) => normalizeArtworkTagText(option.label).toLowerCase() === key
    )
    return exists ? "" : normalized
  }, [onCreateTag, selectOptions, searchText])

  const handleChange = useCallback(
    (tagIds: Uuid[]) => {
      // A controlled `searchValue` defeats antd's own autoClearSearchValue, so
      // selecting or removing a tag clears the search term here instead.
      setSearchText("")
      onChange(tagIds)
    },
    [onChange]
  )

  const handleCreate = useCallback(async () => {
    if (!onCreateTag || !pendingCreateText) return
    setIsCreating(true)
    try {
      const tagId = await onCreateTag(pendingCreateText)
      if (!tagId) {
        message.warning("That tag could not be created.")
        return
      }
      setJustCreated((prev) => [
        ...prev,
        { id: tagId, text: pendingCreateText, usageCount: 0 },
      ])
      setSearchText("")
      if (!value.includes(tagId)) onChange([...value, tagId])
    } finally {
      setIsCreating(false)
    }
  }, [message, onChange, onCreateTag, pendingCreateText, value])

  const dropdownRender = useCallback(
    (menu: React.ReactElement) => (
      <>
        {menu}
        {pendingCreateText ? (
          <>
            <Divider style={{ margin: "8px 0" }} />
            {/*
             * Keeping the default off the mousedown keeps focus in the search
             * input: rc-select clears the search term on blur, which would
             * unmount this button before its click ever landed.
             */}
            <div
              style={{ padding: "0 8px 8px" }}
              onMouseDown={(event) => event.preventDefault()}
            >
              <Button
                type="primary"
                size="small"
                block
                icon={<PlusOutlined />}
                loading={isCreating}
                onClick={() => void handleCreate()}
                data-testid="artwork-tag-create"
              >
                Create “{pendingCreateText}”
              </Button>
            </div>
          </>
        ) : null}
      </>
    ),
    [handleCreate, isCreating, pendingCreateText]
  )

  return (
    <span data-testid={testId} style={style}>
      <Select
        mode="multiple"
        showSearch
        value={value as Uuid[]}
        onChange={handleChange}
        options={selectOptions}
        searchValue={searchText}
        onSearch={setSearchText}
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        dropdownRender={onCreateTag ? dropdownRender : undefined}
        // rc-select suppresses the whole popup when the filtered list is empty
        // AND notFoundContent is falsy — which would hide the create row too.
        notFoundContent={onCreateTag ? "No matching tag" : undefined}
        // The popup is portalled to the body, so nothing in the DOM ties it
        // back to this select. antd leaves a closed popup mounted, so with the
        // editor and the filter both on the page a body-level query can match
        // two identical options and there is no way to tell whose is whose.
        popupClassName={`${testId}-popup`}
        placeholder={placeholder}
        maxTagCount="responsive"
        allowClear={allowClear}
        disabled={disabled}
        aria-label={ariaLabel}
        style={{ width: "100%" }}
      />
    </span>
  )
}

export default ArtworkTagSelect
